import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from 'api';
import type { NotesPayload } from 'api/models/payload/Notes';
import type { NoteInterface } from 'types';

export const notesKeys = {
    all: ['notes'] as const,
    byCase: (appId: number) => ['notes', 'case', appId] as const,
};

export const useNotesByCase = (appId?: number, options?: { enabled?: boolean }) => {
    return useQuery<NoteInterface[]>({
        queryKey: notesKeys.byCase(appId ?? 0),
        queryFn: async () => {
            const response = await api.notes.getNotesByCase(appId as number);
            return (response.data?.data as NoteInterface[]) ?? [];
        },
        enabled: (options?.enabled ?? true) && Boolean(appId),
        staleTime: 60 * 1000,
    });
};

export const useCreateNote = (appId?: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: NotesPayload) => {
            const response = await api.notes.post(payload);
            return response.data;
        },
        onSuccess: () => {
            if (appId) {
                void queryClient.invalidateQueries({ queryKey: notesKeys.byCase(appId) });
            }
        },
    });
};

export const useUpdateNote = (appId?: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: NotesPayload) => {
            const response = await api.notes.put(payload);
            return response.data;
        },
        onSuccess: () => {
            if (appId) {
                void queryClient.invalidateQueries({ queryKey: notesKeys.byCase(appId) });
            }
        },
    });
};

export const useDeleteNotes = (appId?: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (noteIds: number[]) => {
            await Promise.all(noteIds.map((id) => api.notes.deleteNote(id)));
        },
        onSuccess: () => {
            if (appId) {
                void queryClient.invalidateQueries({ queryKey: notesKeys.byCase(appId) });
            }
        },
    });
};
